/**
 * Drill animation playback engine for the new drill canvas.
 *
 * Reads `DrillSpec.keyframes` (v2 — rolePlayers/cones/arrows/ball
 * snapshots) and produces per-frame interpolated state. Each overlay
 * (PlayerRoleOverlay2D, EquipmentOverlay2D, DrawingOverlay2D) reads
 * the interpolated state during playback + falls back to the coach's
 * live edit state when idle.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DrillKeyframe, DrillArrow, DrillCone, NormPoint } from '@4cm/core/drills/types';
import type { DrillRolePlayer } from '../components/drillCanvas/playerRoles';

export type PlaybackMode = 'idle' | 'playing' | 'paused';

export interface PlaybackState {
  mode: PlaybackMode;
  /** Normalised time across the full play 0..1. */
  t: number;
  speed: number;
  loop: boolean;
}

export interface AnimatedDrillState {
  rolePlayers: DrillRolePlayer[];
  cones: DrillCone[];
  arrows: DrillArrow[];
}

export interface DrillPlaybackHook {
  /** Current playback state — bind UI to this. */
  state: PlaybackState;
  /** Interpolated overlay state at the current `t`. null when the drill
   *  has < 2 keyframes (nothing to animate) or when mode === 'idle'. */
  animated: AnimatedDrillState | null;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  reset: () => void;
  setT: (t: number) => void;
  setSpeed: (s: number) => void;
  setLoop: (b: boolean) => void;
}

interface UseDrillPlaybackOpts {
  keyframes: DrillKeyframe[];
  /** Live coach-edit state — used as kf0 when the drill has 1 keyframe
   *  + as the source for any object the keyframes don't snapshot. */
  livePlayers: DrillRolePlayer[];
  liveCones: DrillCone[];
  liveArrows: DrillArrow[];
  /** Default playback speed multiplier. */
  defaultSpeed?: number;
}

/** Linear interpolation. */
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpPoint = (a: NormPoint, b: NormPoint, t: number): NormPoint => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
];

/** Sum durations across keyframes. Each kf's `durationSec` is the time
 *  to TRANSITION INTO this keyframe from the previous one. First kf is
 *  always t=0; total = sum of remaining durations. */
function totalDuration(kfs: DrillKeyframe[]): number {
  if (kfs.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < kfs.length; i += 1) sum += Math.max(0.1, kfs[i].durationSec ?? 1);
  return sum;
}

/** Find the bracketing keyframes for absolute time `tSec` and the
 *  segment-local fraction. */
function bracket(kfs: DrillKeyframe[], tSec: number): { from: DrillKeyframe; to: DrillKeyframe; localT: number } {
  if (kfs.length < 2) return { from: kfs[0], to: kfs[0], localT: 0 };
  let accumulated = 0;
  for (let i = 1; i < kfs.length; i += 1) {
    const segDur = Math.max(0.1, kfs[i].durationSec ?? 1);
    if (tSec <= accumulated + segDur) {
      const localT = segDur === 0 ? 1 : (tSec - accumulated) / segDur;
      return { from: kfs[i - 1], to: kfs[i], localT };
    }
    accumulated += segDur;
  }
  // Past the end — clamp to the last segment fully complete.
  return { from: kfs[kfs.length - 2], to: kfs[kfs.length - 1], localT: 1 };
}

/** Apply objectTiming so per-entity windows can stagger movement within
 *  a single keyframe transition (Demo's OBJECT Start/End slider). */
function objectLocalT(globalLocalT: number, timing?: { startPct: number; endPct: number }): number {
  if (!timing) return globalLocalT;
  const { startPct, endPct } = timing;
  if (globalLocalT <= startPct) return 0;
  if (globalLocalT >= endPct) return 1;
  return (globalLocalT - startPct) / Math.max(0.001, endPct - startPct);
}

/** Per-overlay frame computation. Each function looks up the entity's
 *  position in the to-keyframe, falls back to the from-keyframe, falls
 *  back to the live state. Same pattern as Demo's flipbook. */
function interpRolePlayers(
  from: DrillKeyframe, to: DrillKeyframe, localT: number,
  liveById: Record<string, DrillRolePlayer>,
): DrillRolePlayer[] {
  const ids = new Set([
    ...Object.keys(from.rolePlayers ?? {}),
    ...Object.keys(to.rolePlayers ?? {}),
    ...Object.keys(liveById),
  ]);
  const out: DrillRolePlayer[] = [];
  for (const id of ids) {
    const live = liveById[id];
    if (!live) continue;
    const fromPos = from.rolePlayers?.[id] ?? live.position;
    const toPos = to.rolePlayers?.[id] ?? fromPos;
    const localT2 = objectLocalT(localT, to.objectTiming?.[id]);
    out.push({ ...live, position: lerpPoint(fromPos, toPos, localT2) });
  }
  return out;
}

function interpCones(
  from: DrillKeyframe, to: DrillKeyframe, localT: number,
  liveById: Record<string, DrillCone>,
): DrillCone[] {
  const ids = new Set([
    ...Object.keys(from.cones ?? {}),
    ...Object.keys(to.cones ?? {}),
    ...Object.keys(liveById),
  ]);
  const out: DrillCone[] = [];
  for (const id of ids) {
    const live = liveById[id];
    if (!live) continue;
    const fromPos = from.cones?.[id] ?? live.position;
    const toPos = to.cones?.[id] ?? fromPos;
    const localT2 = objectLocalT(localT, to.objectTiming?.[id]);
    out.push({ ...live, position: lerpPoint(fromPos, toPos, localT2) });
  }
  return out;
}

function interpArrows(
  from: DrillKeyframe, to: DrillKeyframe, localT: number,
  liveById: Record<string, DrillArrow>,
): DrillArrow[] {
  const ids = new Set([
    ...Object.keys(from.arrows ?? {}),
    ...Object.keys(to.arrows ?? {}),
    ...Object.keys(liveById),
  ]);
  const out: DrillArrow[] = [];
  for (const id of ids) {
    const live = liveById[id];
    if (!live) continue;
    // Static fallback — if neither kf snapshots this arrow, keep its
    // live shape.
    const fromSnap = from.arrows?.[id];
    const toSnap = to.arrows?.[id];
    if (!fromSnap && !toSnap) {
      out.push(live);
      continue;
    }
    const fromFrom = fromSnap?.from ?? (live.from.kind === 'point' ? live.from.position : [0.5, 0.5] as NormPoint);
    const fromTo   = fromSnap?.to   ?? (live.to.kind   === 'point' ? live.to.position   : [0.5, 0.5] as NormPoint);
    const toFrom = toSnap?.from ?? fromFrom;
    const toTo   = toSnap?.to   ?? fromTo;
    const localT2 = objectLocalT(localT, to.objectTiming?.[id]);
    out.push({
      ...live,
      from: { kind: 'point', position: lerpPoint(fromFrom, toFrom, localT2) },
      to:   { kind: 'point', position: lerpPoint(fromTo,   toTo,   localT2) },
    });
  }
  return out;
}

export function useDrillPlayback({
  keyframes, livePlayers, liveCones, liveArrows, defaultSpeed = 1,
}: UseDrillPlaybackOpts): DrillPlaybackHook {
  const [state, setState] = useState<PlaybackState>({
    mode: 'idle', t: 0, speed: defaultSpeed, loop: false,
  });
  const rafRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);

  const total = totalDuration(keyframes);

  // Stop loop when keyframes drop below 2.
  useEffect(() => {
    if (keyframes.length < 2 && state.mode !== 'idle') {
      setState((s) => ({ ...s, mode: 'idle' }));
    }
  }, [keyframes.length, state.mode]);

  // rAF tick.
  useEffect(() => {
    if (state.mode !== 'playing' || total <= 0) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      lastTimestampRef.current = null;
      return;
    }
    const tick = (ts: number) => {
      const prev = lastTimestampRef.current ?? ts;
      const deltaSec = ((ts - prev) / 1000) * state.speed;
      lastTimestampRef.current = ts;
      setState((s) => {
        if (s.mode !== 'playing') return s;
        const nextT = Math.min(1, s.t + deltaSec / total);
        if (nextT >= 1) {
          if (s.loop) return { ...s, t: 0 };
          return { ...s, t: 1, mode: 'paused' };
        }
        return { ...s, t: nextT };
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      lastTimestampRef.current = null;
    };
  }, [state.mode, state.speed, total]);

  const play = useCallback(() => {
    setState((s) => ({ ...s, mode: keyframes.length >= 2 ? 'playing' : 'idle', t: s.t >= 1 ? 0 : s.t }));
  }, [keyframes.length]);
  const pause = useCallback(() => setState((s) => ({ ...s, mode: 'paused' })), []);
  const toggle = useCallback(() => setState((s) => (s.mode === 'playing' ? { ...s, mode: 'paused' } : { ...s, mode: 'playing', t: s.t >= 1 ? 0 : s.t })), []);
  const reset = useCallback(() => setState((s) => ({ ...s, mode: 'idle', t: 0 })), []);
  const setT = useCallback((t: number) => setState((s) => ({ ...s, t: Math.max(0, Math.min(1, t)), mode: s.mode === 'playing' ? 'paused' : s.mode })), []);
  const setSpeed = useCallback((sp: number) => setState((s) => ({ ...s, speed: sp })), []);
  const setLoop = useCallback((b: boolean) => setState((s) => ({ ...s, loop: b })), []);

  // Compute animated state. Returns null when nothing is being animated
  // (idle + at t=0 → overlays just render live state directly).
  let animated: AnimatedDrillState | null = null;
  if (keyframes.length >= 2 && (state.mode !== 'idle' || state.t > 0)) {
    const tSec = state.t * total;
    const { from, to, localT } = bracket(keyframes, tSec);
    const liveById = {
      players: Object.fromEntries(livePlayers.map((p) => [p.id, p])),
      cones: Object.fromEntries(liveCones.map((c) => [c.id, c])),
      arrows: Object.fromEntries(liveArrows.map((a) => [a.id, a])),
    };
    animated = {
      rolePlayers: interpRolePlayers(from, to, localT, liveById.players),
      cones: interpCones(from, to, localT, liveById.cones),
      arrows: interpArrows(from, to, localT, liveById.arrows),
    };
  }

  return { state, animated, play, pause, toggle, reset, setT, setSpeed, setLoop };
}

/** Build a v2 keyframe from the current canvas state. Used by the
 *  "Capture" button — snapshots positions for every visible object so
 *  later edits don't drift between keyframes. */
export function captureKeyframe(
  label: string,
  durationSec: number,
  players: DrillRolePlayer[],
  cones: DrillCone[],
  arrows: DrillArrow[],
): DrillKeyframe {
  const rolePlayers: Record<string, NormPoint> = {};
  for (const p of players) rolePlayers[p.id] = p.position;
  const conesMap: Record<string, NormPoint> = {};
  for (const c of cones) conesMap[c.id] = c.position;
  const arrowsMap: Record<string, { from: NormPoint; to: NormPoint }> = {};
  for (const a of arrows) {
    const f = a.from.kind === 'point' ? a.from.position : [0.5, 0.5] as NormPoint;
    const t = a.to.kind === 'point' ? a.to.position : [0.5, 0.5] as NormPoint;
    arrowsMap[a.id] = { from: f, to: t };
  }
  return {
    id: `kf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`,
    label,
    durationSec,
    positions: {}, // legacy field — v2 readers ignore
    rolePlayers,
    cones: conesMap,
    arrows: arrowsMap,
  };
}
